/**
 * continuousEffectEnginePure - 純粋関数版常時効果エンジン
 *
 * グローバル状態を持たず、フィールドから動的に常時効果を計算する。
 * シミュレーションで使用可能な純粋関数版。
 *
 * @see src/engine/continuousEffects/effectEngine.js - 元の実装
 * @see src/ルール/continuous-effect-system-design.md
 */

import { allEffectDefinitions, phaseCardEffectsByStage } from '../continuousEffects/effectDefinitions';
import { CONTINUOUS_EFFECT_TYPES } from '../continuousEffects/effectTypes';
import { TARGET_TYPES } from '../continuousEffects/targetTypes';
import { checkCondition } from '../continuousEffects/conditionChecker';
import { calculateValue, setEffectUniqueId } from '../continuousEffects/valueCalculator';
import type { GameState } from './GameState';
import type { MonsterInstance, CardWithId, CardData, PhaseCardInstance, FieldCardInstance } from '../../types/card';
import type { PlayerNumber } from '../../types/player';

// ========================================
// 型定義
// ========================================

/** 効果定義 */
interface EffectDefinition {
  type: string;
  target?: string;
  condition?: Record<string, unknown>;
  value?: number;
  damageType?: string;
  buffType?: 'ATK' | 'HP' | 'BOTH';
  [key: string]: unknown;
}

/** 効果エントリ */
interface EffectWithContext extends EffectDefinition {
  uniqueId: string;
  cardId: string;
  owner: PlayerNumber;
  stage?: number;
}

/** 収集された効果エントリ */
interface EffectEntry {
  card: MonsterInstance | FieldCardInstance | PhaseCardInstance | CardData;
  owner: PlayerNumber;
  effects: EffectWithContext[];
  slotIndex?: number;
  stage?: number;
}

/** 効果計算コンテキスト */
interface EffectContext {
  currentPlayer: PlayerNumber;
  p1Field: (MonsterInstance | null)[];
  p2Field: (MonsterInstance | null)[];
  p1Life: number;
  p2Life: number;
  effectOwner: PlayerNumber;
  /** 拡張用（ConditionContext, ValueCalculatorContextとの互換性） */
  [key: string]: unknown;
}

/** 召喚時バフ結果 */
interface SummonBuffs {
  atkBuff: number;
  hpBuff: number;
}

/** 効果定義レジストリ */
type EffectDefinitionsRegistry = Record<string, EffectDefinition[]>;

/** フェイズカード段階別効果 */
type PhaseCardEffectsByStage = Record<string, Record<number, EffectDefinition[]>>;

// UIと同じ定義を参照する。存在しないexport名を空配列にフォールバックしない。
const effectDefinitions = allEffectDefinitions as EffectDefinitionsRegistry;
const phaseCardEffects = phaseCardEffectsByStage as PhaseCardEffectsByStage;

// ========================================
// フィールドから効果を収集
// ========================================

/**
 * フィールド上のカードから常時効果を収集
 */
function collectActiveEffects(state: GameState): EffectEntry[] {
  const entries: EffectEntry[] = [];

  // P1のフィールドカード
  if (state.p1.fieldCard) {
    const effects = getCardEffects(state.p1.fieldCard, 1);
    if (effects.length > 0) {
      entries.push({ card: state.p1.fieldCard, owner: 1, effects });
    }
  }

  // P2のフィールドカード
  if (state.p2.fieldCard) {
    const effects = getCardEffects(state.p2.fieldCard, 2);
    if (effects.length > 0) {
      entries.push({ card: state.p2.fieldCard, owner: 2, effects });
    }
  }

  // P1のフィールドモンスター
  state.p1.field.forEach((monster, idx) => {
    if (monster) {
      const effects = getCardEffects(monster, 1);
      if (effects.length > 0) {
        entries.push({ card: monster, owner: 1, effects, slotIndex: idx });
      }
    }
  });

  // P2のフィールドモンスター
  state.p2.field.forEach((monster, idx) => {
    if (monster) {
      const effects = getCardEffects(monster, 2);
      if (effects.length > 0) {
        entries.push({ card: monster, owner: 2, effects, slotIndex: idx });
      }
    }
  });

  // P1のフェイズカード
  if (state.p1.phaseCard) {
    const stage = state.p1.phaseCard.stage ?? state.p1.phaseCard.stackCount ?? state.p1.phaseCard.charges?.length ?? 0;
    const effects = getPhaseCardEffects(state.p1.phaseCard, 1, stage);
    if (effects.length > 0) {
      entries.push({ card: state.p1.phaseCard, owner: 1, effects, stage });
    }
  }

  // P2のフェイズカード
  if (state.p2.phaseCard) {
    const stage = state.p2.phaseCard.stage ?? state.p2.phaseCard.stackCount ?? state.p2.phaseCard.charges?.length ?? 0;
    const effects = getPhaseCardEffects(state.p2.phaseCard, 2, stage);
    if (effects.length > 0) {
      entries.push({ card: state.p2.phaseCard, owner: 2, effects, stage });
    }
  }

  return entries;
}

/** カードまたはモンスター型 */
type CardLike = MonsterInstance | FieldCardInstance | PhaseCardInstance | CardData;

/**
 * カードの常時効果定義を取得
 */
function getCardEffects(card: CardLike, owner: PlayerNumber): EffectWithContext[] {
  if (!card || !card.id) return [];

  const definitions = effectDefinitions?.[card.id];
  if (!definitions) return [];

  return definitions.map((def) => ({
    ...def,
    uniqueId: (card as { uniqueId?: string }).uniqueId || card.id,
    cardId: card.id,
    owner,
  }));
}

/**
 * フェイズカードの段階別常時効果定義を取得
 */
function getPhaseCardEffects(card: PhaseCardInstance, owner: PlayerNumber, stage: number): EffectWithContext[] {
  if (!card || !card.id) return [];

  const stageEffects = phaseCardEffects[card.id];
  if (!stageEffects) return [];

  const definitions = stageEffects[stage];
  if (!definitions) return [];

  return definitions.map((def) => ({
    ...def,
    uniqueId: card.uniqueId,
    cardId: card.id,
    owner,
    stage,
  }));
}

// ========================================
// ターゲットチェック
// ========================================

/**
 * 効果のターゲットをチェック
 */
function checkTarget(effect: EffectWithContext, targetMonster: MonsterInstance | null, effectOwner: PlayerNumber, state: GameState): boolean {
  if (!effect.target) return true;
  if (!targetMonster) return false;

  const targetOwner: PlayerNumber = targetMonster.owner || (
    state.p1.field.some((m) => m && m.uniqueId === targetMonster.uniqueId) ? 1 : 2
  );
  const isOwnMonster: boolean = targetOwner === effectOwner;

  switch (effect.target) {
    case TARGET_TYPES.SELF_CARD:
      return targetMonster.uniqueId === effect.uniqueId;

    case TARGET_TYPES.SELF_MONSTERS:
      return isOwnMonster;

    case TARGET_TYPES.OPPONENT_MONSTERS:
      return !isOwnMonster;

    case TARGET_TYPES.ALL_MONSTERS:
      return true;

    case 'self_monsters_except_self':
      return isOwnMonster && targetMonster.uniqueId !== effect.uniqueId;

    default:
      return true;
  }
}

// ========================================
// 計算関数（純粋関数版）
// ========================================

/**
 * 攻撃力修正値を計算（純粋関数版）
 */
export function calculateAttackModifierPure(state: GameState, monster: MonsterInstance): number {
  const entries = collectActiveEffects(state);
  let totalModifier = 0;

  for (const { card, owner, effects } of entries) {
    const effectContext: EffectContext = {
      currentPlayer: state.currentPlayer,
      p1Field: state.p1.field,
      p2Field: state.p2.field,
      p1Life: state.p1.life,
      p2Life: state.p2.life,
      effectOwner: owner,
    };

    for (const effect of effects) {
      if (effect.type !== CONTINUOUS_EFFECT_TYPES.ATK_MODIFIER) continue;

      if (!checkTarget(effect, monster, owner, state)) continue;
      if (!checkCondition(effect.condition, monster, effectContext)) continue;

      const effectWithId = setEffectUniqueId(effect, (card as { uniqueId?: string }).uniqueId || card.id);
      totalModifier += calculateValue(effectWithId, effectContext);
    }
  }

  return totalModifier;
}

/**
 * HP修正値を計算（純粋関数版）
 */
export function calculateHPModifierPure(state: GameState, monster: MonsterInstance): number {
  const entries = collectActiveEffects(state);
  let totalModifier = 0;

  for (const { card, owner, effects } of entries) {
    const effectContext: EffectContext = {
      currentPlayer: state.currentPlayer,
      p1Field: state.p1.field,
      p2Field: state.p2.field,
      p1Life: state.p1.life,
      p2Life: state.p2.life,
      effectOwner: owner,
    };

    for (const effect of effects) {
      if (effect.type !== CONTINUOUS_EFFECT_TYPES.HP_MODIFIER) continue;

      if (!checkTarget(effect, monster, owner, state)) continue;
      if (!checkCondition(effect.condition, monster, effectContext)) continue;

      const effectWithId = setEffectUniqueId(effect, (card as { uniqueId?: string }).uniqueId || card.id);
      totalModifier += calculateValue(effectWithId, effectContext);
    }
  }

  return totalModifier;
}

/**
 * 召喚コスト修正値を計算（純粋関数版）
 */
export function calculateSummonCostModifierPure(state: GameState, cardToSummon: CardWithId | CardData, summoner: PlayerNumber): number {
  const entries = collectActiveEffects(state);
  let totalModifier = 0;

  for (const { card, owner, effects } of entries) {
    // 自分のカードからのコスト修正のみ
    if (owner !== summoner) continue;

    const effectContext: EffectContext = {
      currentPlayer: state.currentPlayer,
      p1Field: state.p1.field,
      p2Field: state.p2.field,
      p1Life: state.p1.life,
      p2Life: state.p2.life,
      effectOwner: owner,
    };

    for (const effect of effects) {
      if (effect.type !== CONTINUOUS_EFFECT_TYPES.SUMMON_COST_MODIFIER) continue;

      // 召喚するカードに対する条件チェック
      if (!checkCondition(effect.condition, cardToSummon, effectContext)) continue;

      const effectWithId = setEffectUniqueId(effect, (card as { uniqueId?: string }).uniqueId || card.id);
      totalModifier += calculateValue(effectWithId, effectContext);
    }
  }

  return totalModifier;
}

/** ダメージタイプ */
type DamageType = 'battle' | 'effect' | 'direct';

/**
 * ダメージ軽減値を計算（純粋関数版）
 */
export function calculateDamageReductionPure(state: GameState, target: MonsterInstance, damageType: DamageType = 'battle'): number {
  const entries = collectActiveEffects(state);
  let totalReduction = 0;

  for (const { card, owner, effects } of entries) {
    const effectContext: EffectContext = {
      currentPlayer: state.currentPlayer,
      p1Field: state.p1.field,
      p2Field: state.p2.field,
      p1Life: state.p1.life,
      p2Life: state.p2.life,
      effectOwner: owner,
    };

    for (const effect of effects) {
      if (effect.type !== CONTINUOUS_EFFECT_TYPES.DAMAGE_REDUCTION) continue;

      // ダメージタイプチェック
      if (effect.damageType && effect.damageType !== damageType) continue;

      if (!checkTarget(effect, target, owner, state)) continue;
      if (!checkCondition(effect.condition, target, effectContext)) continue;

      const effectWithId = setEffectUniqueId(effect, (card as { uniqueId?: string }).uniqueId || card.id);
      totalReduction += calculateValue(effectWithId, effectContext);
    }
  }

  return totalReduction;
}

/**
 * 召喚時バフを取得（純粋関数版）
 */
export function getSummonBuffsPure(state: GameState, monster: MonsterInstance, summoner: PlayerNumber): SummonBuffs {
  const entries = collectActiveEffects(state);
  let atkBuff = 0;
  let hpBuff = 0;

  for (const { card, owner, effects } of entries) {
    // 自分のカードからのバフのみ
    if (owner !== summoner) continue;

    const effectContext: EffectContext = {
      currentPlayer: state.currentPlayer,
      p1Field: state.p1.field,
      p2Field: state.p2.field,
      p1Life: state.p1.life,
      p2Life: state.p2.life,
      effectOwner: owner,
    };

    for (const effect of effects) {
      if (effect.type !== CONTINUOUS_EFFECT_TYPES.ON_SUMMON_BUFF) continue;

      if (!checkCondition(effect.condition, monster, effectContext)) continue;

      const effectWithId = setEffectUniqueId(effect, (card as { uniqueId?: string }).uniqueId || card.id);
      const buffValue: number = calculateValue(effectWithId, effectContext);

      if (effect.buffType === 'ATK') {
        atkBuff += buffValue;
      } else if (effect.buffType === 'HP') {
        hpBuff += buffValue;
      } else if (effect.buffType === 'BOTH') {
        atkBuff += buffValue;
        hpBuff += buffValue;
      }
    }
  }

  return { atkBuff, hpBuff };
}

/**
 * 攻撃可能かチェック（純粋関数版）
 */
export function canAttackPure(state: GameState, monster: MonsterInstance): boolean {
  const entries = collectActiveEffects(state);

  for (const { owner, effects } of entries) {
    const effectContext: EffectContext = {
      currentPlayer: state.currentPlayer,
      p1Field: state.p1.field,
      p2Field: state.p2.field,
      p1Life: state.p1.life,
      p2Life: state.p2.life,
      effectOwner: owner,
    };

    for (const effect of effects) {
      if (effect.type !== CONTINUOUS_EFFECT_TYPES.ATTACK_RESTRICTION) continue;

      if (!checkTarget(effect, monster, owner, state)) continue;
      if (!checkCondition(effect.condition, monster, effectContext)) continue;

      // 攻撃制限が適用される
      return false;
    }
  }

  return true;
}

/**
 * 魔法カードが無効化されるかチェック（純粋関数版）
 */
export function isMagicNegatedPure(state: GameState, magicCard: CardWithId | CardData, caster: PlayerNumber): boolean {
  const entries = collectActiveEffects(state);

  for (const { owner, effects } of entries) {
    // 相手の効果のみ
    if (owner === caster) continue;

    const effectContext: EffectContext = {
      currentPlayer: state.currentPlayer,
      p1Field: state.p1.field,
      p2Field: state.p2.field,
      p1Life: state.p1.life,
      p2Life: state.p2.life,
      effectOwner: owner,
    };

    for (const effect of effects) {
      if (effect.type !== CONTINUOUS_EFFECT_TYPES.MAGIC_NEGATION) continue;

      if (!checkCondition(effect.condition, magicCard, effectContext)) continue;

      return true;
    }
  }

  return false;
}

// ========================================
// ユーティリティ
// ========================================

/**
 * 全効果エントリーを取得（デバッグ用）
 */
export function getAllActiveEffectsPure(state: GameState): EffectEntry[] {
  return collectActiveEffects(state);
}

/** デフォルトエクスポート */
interface ContinuousEffectEnginePure {
  calculateAttackModifierPure: typeof calculateAttackModifierPure;
  calculateHPModifierPure: typeof calculateHPModifierPure;
  calculateSummonCostModifierPure: typeof calculateSummonCostModifierPure;
  calculateDamageReductionPure: typeof calculateDamageReductionPure;
  getSummonBuffsPure: typeof getSummonBuffsPure;
  canAttackPure: typeof canAttackPure;
  isMagicNegatedPure: typeof isMagicNegatedPure;
  getAllActiveEffectsPure: typeof getAllActiveEffectsPure;
}

const continuousEffectEnginePure: ContinuousEffectEnginePure = {
  calculateAttackModifierPure,
  calculateHPModifierPure,
  calculateSummonCostModifierPure,
  calculateDamageReductionPure,
  getSummonBuffsPure,
  canAttackPure,
  isMagicNegatedPure,
  getAllActiveEffectsPure,
};

export default continuousEffectEnginePure;
